import { type Control, type FieldValues, useWatch } from "react-hook-form";

import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useCheckSlugAvailabilityQuery } from "@/services/gamification";

import {
  isCampaignSlug,
  SLUG_CHECK_DEBOUNCE_MS,
  SlugStatus,
} from "../constants/campaign-slug";
import type { CampaignSlugFormValues } from "./use-campaign-slug-suggestion";

export interface SlugAvailability {
  slug: string;
  status: SlugStatus;
  retry: () => void;
}

interface SlugCheckSnapshot {
  slug: string;
  checkedSlug: string;
  isFetching: boolean;
  isError: boolean;
  answeredSlug: string | undefined;
  available: boolean | undefined;
}

export function resolveSlugStatus(snapshot: SlugCheckSnapshot): SlugStatus {
  if (!snapshot.slug) return SlugStatus.EMPTY;
  if (!isCampaignSlug(snapshot.slug)) return SlugStatus.INVALID;
  if (snapshot.slug !== snapshot.checkedSlug || snapshot.isFetching) {
    return SlugStatus.CHECKING;
  }
  if (snapshot.isError) return SlugStatus.FAILED;
  if (snapshot.answeredSlug !== snapshot.slug) return SlugStatus.CHECKING;
  return snapshot.available ? SlugStatus.AVAILABLE : SlugStatus.TAKEN;
}

export function useSlugAvailability<T extends CampaignSlugFormValues>(
  control: Control<T>,
  excludeId?: number,
): SlugAvailability {

  const watched: unknown = useWatch({
    control: control as unknown as Control<FieldValues>,
    name: "slug",
  });
  const slug: string = typeof watched === "string" ? watched.trim() : "";
  const checkedSlug: string = useDebouncedValue<string>(
    slug,
    SLUG_CHECK_DEBOUNCE_MS,
  );
  const { data, isFetching, isError, refetch } = useCheckSlugAvailabilityQuery(
    { slug: checkedSlug, excludeId },
    { skip: !isCampaignSlug(checkedSlug) },
  );

  const status: SlugStatus = resolveSlugStatus({
    slug,
    checkedSlug,
    isFetching,
    isError,
    answeredSlug: data?.slug,
    available: data?.available,
  });

  return { slug, status, retry: refetch };
}
